import {
    AddRounded,
    CloseRounded,
    EmailOutlined,
    LocalOfferOutlined,
    LocationOnOutlined,
    PhoneOutlined,
    SvgIconComponent,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    CardProps,
    Chip,
    Link,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import addressFormatter from '@fragaria/address-formatter';
import SelectLabel from './SelectLabel';
import { useState } from 'react';
import { hueToLabelColor } from '@utils/contactUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@api/axiosInstance';

interface DetailItemProps {
    Icon: SvgIconComponent;
    children?: React.ReactNode;
}

const DetailItem: React.FC<DetailItemProps> = ({ Icon, isNull, children }) => {
    if (isNull) return null;
    return (
        <Box display="flex" gap={2}>
            <Icon fontSize="small" sx={{ mt: 0.25 }} />
            <Box display="flex" flexDirection="column" gap={0.5}>
                {children}
            </Box>
        </Box>
    );
};

interface ContactInfoCardProps extends CardProps {
    contact: any;
}

const ContactInfoCard = ({ contact, sx, ...props }: ContactInfoCardProps) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [labelAnchorEl, setLabelAnchorEl] = useState(null);

    const address = addressFormatter.format({
        street: contact.street,
        houseNumber: contact.house_number,
        city: contact.city,
        country: contact.country?.toCountryDisplayName(),
        state: contact.state,
        neighbourhood: contact.neighbourhood,
        postcode: contact.zip_code,
    });

    const { mutate: removeLabel } = useMutation({
        mutationFn: label =>
            axiosInstance.post('contacts/labels/remove', {
                contact_ids: [contact.contact_id],
                label_name: label,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries(['contact', contact.contact_id]);
        },
    });

    return (
        <Card
            elevation={0}
            sx={{
                mx: 0,
                ...sx,
            }}
            {...props}
        >
            <CardHeader
                title={t('Contact Details')}
                subheader={
                    <>
                        {contact.created && (
                            <>
                                {t('Added')}:&nbsp;
                                {new Date(contact.created).toLocaleDateString()}
                                <br />
                            </>
                        )}
                        {contact.updated && (
                            <>
                                {t('Edited')}:&nbsp;
                                {new Date(contact.updated).toLocaleDateString()}
                            </>
                        )}
                    </>
                }
            />
            <CardContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Typography variant="bodyLarge">
                    {contact.description}
                </Typography>
                <DetailItem
                    Icon={EmailOutlined}
                    isNull={!contact.email_addresses.length}
                >
                    {contact.email_addresses.map(email => (
                        <Link key={email} href={`mailto:${email}`}>
                            {email}
                        </Link>
                    ))}
                </DetailItem>
                <DetailItem
                    Icon={PhoneOutlined}
                    isNull={!contact.phone_numbers.length}
                >
                    {contact.phone_numbers.map(phone => (
                        <Link key={phone} href={`tel:${phone}`}>
                            {phone}
                        </Link>
                    ))}
                </DetailItem>
                <DetailItem Icon={LocationOnOutlined} isNull={!address.trim()}>
                    <Typography>{address}</Typography>
                </DetailItem>
                <DetailItem Icon={LocalOfferOutlined}>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {contact.labels.map(label => (
                            <Chip
                                sx={{
                                    backgroundColor: hueToLabelColor(label.hue),
                                }}
                                key={label.name}
                                label={label.name}
                                deleteIcon={<CloseRounded />}
                                onDelete={() => removeLabel(label.name)}
                            />
                        ))}
                        <Chip
                            label={t('Label')}
                            variant="outlined"
                            color="primary"
                            onClick={e => setLabelAnchorEl(e.currentTarget)}
                            icon={<AddRounded />}
                        />
                        <SelectLabel
                            anchorEL={labelAnchorEl}
                            onClose={() => setLabelAnchorEl(null)}
                            currentLabels={contact.labels}
                        />
                    </Box>
                </DetailItem>
            </CardContent>
        </Card>
    );
};

export default ContactInfoCard;
